import {useState, type ReactNode} from 'react'
import {Link, NavLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {CloseGlyph, MenuGlyph} from '@/components/ui/icons'
import {KairosMark} from '@/components/kairos-mark'
import {SiteHeaderActions} from '@/components/site-header-actions'
import {useAuth} from '@/hooks/platform/use-auth'
import {MANUAL_GRUPOS} from '@/manual'
import {cn} from '@/lib/utils'

/** Chrome for the public /manual routes, deliberately not AppLayout: there is no tenant session. */
export function ManualLayout({children}: {children: ReactNode}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const [navOpen, setNavOpen] = useState(false)

  return (
    <div className="min-h-screen bg-background">
      {/* Alto fijo (`h-16`): el rail del índice se pega debajo con `lg:top-16` y
          `100dvh-4rem`. */}
      <header className="sticky top-0 z-30 border-b border-hairline-on-ink bg-ink-deep text-ink-deep-foreground">
        <div className="flex h-16 items-center gap-3 px-4 md:px-8">
          <Link to="/" className="flex items-center gap-2 text-ink-deep-foreground" data-testid="manual-home-link">
            <KairosMark />
            <span className="font-heading text-lg tracking-tight">Kairos</span>
          </Link>
          <span className="hidden font-mono text-[10px] font-medium tracking-[0.14em] text-ink-deep-foreground/60 uppercase lg:inline">
            {t('manual.eyebrow')}
          </span>
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-expanded={navOpen}
            aria-controls="manual-nav"
            aria-label={t('manual.toggleNav')}
            data-testid="manual-nav-toggle"
            className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-ink-deep-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 hover:bg-white/10 lg:hidden">
            <MenuGlyph />
          </button>
          <div className="ml-auto">
            {user ? (
              <Link
                to="/"
                className="inline-flex min-h-11 items-center whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-button transition-colors hover:bg-primary-hover sm:px-4"
                data-testid="manual-go-to-app">
                {t('manual.goToApp')}
              </Link>
            ) : (
              <SiteHeaderActions tone="ink" compact />
            )}
          </div>
        </div>
      </header>

      <div className="flex items-start">
        {navOpen && (
          <div
            data-testid="manual-nav-scrim"
            className="scrim-backdrop fixed inset-0 z-40 lg:hidden"
            onClick={() => setNavOpen(false)}
          />
        )}
        <aside
          id="manual-nav"
          data-testid="manual-nav"
          className={cn(
            'w-72 shrink-0 overflow-y-auto border-r border-border px-4 py-8 md:px-6',
            'lg:sticky lg:top-16 lg:block lg:h-[calc(100dvh-4rem)]',
            'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-50 max-lg:h-dvh max-lg:bg-card max-lg:shadow-pop max-lg:motion-safe:transition-transform',
            navOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'
          )}>
          <div className="flex items-center justify-between lg:hidden">
            <span className="font-mono text-[10px] font-medium tracking-[0.14em] text-muted-foreground uppercase">
              {t('manual.eyebrow')}
            </span>
            <button
              type="button"
              onClick={() => setNavOpen(false)}
              aria-label={t('manual.closeNav')}
              data-testid="manual-nav-close"
              className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 hover:bg-secondary/60">
              <CloseGlyph />
            </button>
          </div>
          <nav className="mt-4 flex flex-col gap-6 lg:mt-0">
            {MANUAL_GRUPOS.map((grupo, gi) => (
              <div key={grupo.id}>
                <p className="flex items-baseline gap-1.5 font-mono text-[10px] font-medium tracking-[0.14em] text-muted-foreground/70 uppercase">
                  <span className="text-primary">{String(gi + 1).padStart(2, '0')}</span>
                  {grupo.titulo}
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {grupo.secciones.map((seccion) => (
                    <li key={seccion.id}>
                      <NavLink
                        to={`/manual/${seccion.id}`}
                        onClick={() => setNavOpen(false)}
                        data-testid={`manual-nav-link-${seccion.id}`}
                        className={({isActive}) =>
                          cn(
                            'flex min-h-11 items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50 lg:min-h-0',
                            isActive
                              ? 'bg-secondary font-medium text-foreground'
                              : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
                          )
                        }>
                        {({isActive}) => (
                          <>
                            <span
                              aria-hidden="true"
                              className={cn(
                                'h-[5px] w-[5px] shrink-0 rounded-full',
                                isActive ? 'bg-primary' : 'bg-transparent'
                              )}
                            />
                            <span className="min-w-0 flex-1 truncate">{seccion.titulo}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* No max-w: player and step cards sit side by side; each page caps its own text. */}
        <main className="min-w-0 flex-1 px-4 py-10 md:px-8 md:py-14">{children}</main>
      </div>

      <footer className="border-t border-hairline-on-ink bg-ink-deep">
        <div className="flex flex-col items-start gap-3 px-4 py-8 text-xs text-ink-deep-foreground/60 sm:flex-row sm:items-center sm:justify-between md:px-8">
          <div className="flex items-center gap-2 text-ink-deep-foreground">
            <KairosMark />
            <span className="font-heading text-sm tracking-tight">Kairos</span>
          </div>
          <p>{t('manual.footer')}</p>
        </div>
      </footer>
    </div>
  )
}
